import { ReactElement } from 'react';

import { Inject } from '@odinjs/odin';

import { Endpoint, Report, ReportService, ToastServiceStipulator } from '@philips-emr/hpms-stipulator-commons';
import StipulatorContext from '@philips-emr/hpms-stipulator-commons/lib/contexts/stipulator-context';
import FilterService from '@philips-emr/hpms-stipulator-commons/lib/services/filter-service';
import { HPMSOnCreateMenu, HPMSOnCreateOption, HPMSOnOptionClick } from '@philips-emr/hpms-stipulator-components/lib/events/event';
import { HpmsCustomCardEvent, HpmsCustomCardMenuRenderEvent } from '@philips-emr/hpms-stipulator-components/lib/types';
import { FetchAPI } from '@philips-emr/tws-components';
import { ModalButtonType } from '@philips-emr/tws-constants';
import { Value } from '@philips-emr/tws-core';
import Controller, { OnBeforeFetch, OnDynamicContentRender, OnMount, OnSubmit, OnUnmount } from '@philips-emr/tws-core/lib/client';
import { MountEvent, RenderEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService, MessageService, ModalService } from '@philips-emr/tws-core/lib/services';
import { ModalButton, ModalProps } from '@philips-emr/tws-core/lib/services/modal-service';

import { ConsultaSegundaViaAliases as Aliases, FragmentNames } from '../../codegen';
import BaseController from '../BaseController';
import { getLoteStampBySituacao } from '../lote/lote-utils';

interface Data {
  nrSeqBeneficiario: number;
  dsSituacao: string;
  ieSituacao: any;
  dsObservacaoRejeicao: string;
  nrSeqSolicCart: number;
  nrSeqCarteira: number;
}

const OptionKey = {
  RECEBIDO: 'recebido',
  IMPRIMIR: 'imprimir',
  DETALHES: 'detalhes',
};

@Controller({ fragment: FragmentNames.CONSULTA_SEGUNDA_VIA })
class CartaoConsultarSolicitacoesController extends BaseController {

  @Inject('fetch-consulta')
  private fetchConsulta: FetchAPI;

  private reportService: ReportService;

  private exibir: boolean;

  @OnMount(Aliases.FILTER_CONSULTA)
  onFilterMount(event: MountEvent): void {
    FilterService.restoreFilterState(event);
  }

  @OnUnmount(Aliases.FILTER_CONSULTA)
  onUnmountFilter(event: MountEvent): void {
    FilterService.persistFilterState(event);
  }

  @OnSubmit(Aliases.FILTER_CONSULTA)
  onSubmitFilter(): void {
    void this.exibirImprimir().then((resp: boolean) => this.exibir = resp);

  }

  @OnDynamicContentRender(Aliases.HPMS_CUSTOM_CARD)
  onDynamicContentRenderCardLote(event: RenderEvent): void {
    const { data, render } = event;
    const { dsSituacao } = data as Data;
    let { ieSituacao } = data as Data;

    switch (ieSituacao) {
      case '1':
        ieSituacao = 'A';
        break;
      case '2':
        ieSituacao = 'L';
        break;
      case '3':
        ieSituacao = 'L';
        break;
      case '4':
        ieSituacao = 'IP';
        break;
      case '5':
        ieSituacao = 'RO';
        break;

    }
    const content = (): ReactElement => (getLoteStampBySituacao(ieSituacao, dsSituacao));

    render(content);
  }

  @OnBeforeFetch(Aliases.FETCH_CONSULTA)
  onBeforeFetchBeneficiarios(event: MountEvent): void {
    const fetch = event.target as unknown as FetchAPI;
    const { nrSeqSubestipulante } = StipulatorContext.getCurrentContract();
    nrSeqSubestipulante && fetch.setParameters({ ...fetch.getParameters(), nrSeqSubestipulante });
  }

  @HPMSOnCreateMenu(Aliases.HPMS_CUSTOM_CARD)
  onCreateCardMenu(event: HpmsCustomCardMenuRenderEvent<Record<string, Value>>): void {
    const { data, shouldRender } = event;
    const { ieSituacao } = data as unknown as Data;
    shouldRender(this.exibir || ieSituacao == '3' || ieSituacao == '5');
  }

  @HPMSOnCreateOption(Aliases.HPMS_CUSTOM_CARD)
  onCreateOption(event: HpmsCustomCardMenuRenderEvent<Record<string, Value>>): void {
    const { data, key, shouldRender } = event;
    const { ieSituacao } = data as unknown as Data;

    const show = {
      [OptionKey.IMPRIMIR]: this.exibir,
      [OptionKey.RECEBIDO]: ieSituacao == '3',
      [OptionKey.DETALHES]: ieSituacao == '5',
    };

    shouldRender(show[key]);
  }

  @HPMSOnOptionClick(Aliases.HPMS_CUSTOM_CARD)
  onOptionClick(event: HpmsCustomCardEvent<Record<string, Value>>): void {
    const { key } = event;
    const { nrSeqBeneficiario, nrSeqSolicCart, nrSeqCarteira } = event.data as unknown as Data;
    this.context.set({ nrSeqBeneficiario });
    const action = {
      [OptionKey.RECEBIDO]: () => this.onConfirmarRecebimento(),
      [OptionKey.DETALHES]: () => ModalService.openDefault(this.modalMotivoRejeicao(nrSeqSolicCart)),
      [OptionKey.IMPRIMIR]: () => this.imprimir(nrSeqCarteira),

    };

    if (action[key]) {
      action[key]();
    }
  }

  private onConfirmarRecebimento(): void {
    ModalService.openDialog(this.newModalDefaultContent());
  }

  private newModalDefaultContent(): ModalProps {
    const modalDefaultContent: ModalProps = {
      closeable: false,
      dockButtons: this.newDockButtons(),
      title: MessageService.translate('stipulator.confirma-recebimento-segunda-via'),
    };

    return modalDefaultContent;
  }

  private newDockButtons(): ModalButton[] {

    const onOk = (): void => {
      const { nrSeqBeneficiario, nrSeqContrato } = this.context.getBulkData();

      HttpService.post(Endpoint.POST.CARTAO_CONFIRMAR, { nrSeqBeneficiario, nrSeqContrato })
        .then(response => {
          const mensagem = MessageService.translate('stipulator.aviso-solicitacao-realizada').concat(response.body as string);
          ToastServiceStipulator.success(mensagem);
          this.fetchConsulta.forceFetch();
        })
        .catch(() => {
          ToastServiceStipulator.warning(MessageService.translate('stipulator.general-error'));
        });
    };

    const onCancel = (): void => {
      this.fetchConsulta.forceFetch();
    };

    const dockButtons: ModalButton[] = [
      {
        action: onCancel,
        title: MessageService.translate('stipulator.cancelar'),
        type: ModalButtonType.CANCEL,
      },
      {
        action: onOk,
        title: MessageService.translate('stipulator.confirmar'),
        type: ModalButtonType.SUCCESS,
      },
    ];

    return dockButtons;

  }

  private modalMotivoRejeicao(nrSeqSolicCart: number): ModalProps {

    const { nrSeqContrato } = this.historyService.getCurrentRouteParams();
    const modalWithFragment: ModalProps = {
      closeable: true,
      fragment: 'motivo-rejeicao',
      title: 'stipulator.solicitacao-rejeitada-pela-operadora',
      contextData: { nrSeqContrato, nrSeqSolicCart },
    };
    return modalWithFragment;
  }

  private imprimir(nrSequencia: number): void {
    this.reportService = new ReportService('18', Endpoint.GET.RELATORIOS_POR_GRUPO);
    this.reportService.abreListaRelatorios({ NR_SEQUENCIA: nrSequencia });
  }

  private exibirImprimir = async (): Promise<boolean> => {
    const service = new ReportService('18', Endpoint.GET.RELATORIOS_POR_GRUPO);
    const relatorios = await service.getRelatoriosByCdGrupo() as unknown as Report[];
    const exibe = relatorios.length === 0 ? false : true;
    return exibe;
  };

}

export default CartaoConsultarSolicitacoesController;
